import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DTE_TYPE_LABELS } from "../types";
import type { ReferenceInput, DteTypeCode } from "../types";

interface ReferenceSelectorProps {
  value: ReferenceInput | undefined;
  onChange: (value: ReferenceInput | undefined) => void;
  allowedDocTypes?: DteTypeCode[];
}

const REFERENCE_CODES: Array<{ value: 1 | 2 | 3; label: string }> = [
  { value: 1, label: "Anula documento" },
  { value: 2, label: "Corrige texto" },
  { value: 3, label: "Corrige montos" },
];

export function ReferenceSelector({
  value,
  onChange,
  allowedDocTypes = [33, 34, 52, 56, 61],
}: ReferenceSelectorProps) {
  const handleChange = (field: keyof ReferenceInput, fieldValue: unknown) => {
    if (!value) {
      // Initialize with defaults
      onChange({
        tipoDocRef: 33,
        folioRef: 0,
        fechaRef: new Date().toISOString().split("T")[0],
        codigoRef: 1,
        razonRef: "",
        [field]: fieldValue,
      } as ReferenceInput);
    } else {
      onChange({ ...value, [field]: fieldValue });
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Documento de Referencia</h3>
      <p className="text-sm text-muted-foreground">
        Requerido para Notas de Crédito y Débito
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Document Type */}
        <div className="space-y-2">
          <Label>Tipo de Documento</Label>
          <Select
            value={value?.tipoDocRef?.toString() ?? ""}
            onValueChange={(v) =>
              handleChange("tipoDocRef", parseInt(v) as DteTypeCode)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar tipo" />
            </SelectTrigger>
            <SelectContent>
              {allowedDocTypes.map((code) => (
                <SelectItem key={code} value={code.toString()}>
                  {DTE_TYPE_LABELS[code]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Folio */}
        <div className="space-y-2">
          <Label htmlFor="folioRef">Folio</Label>
          <Input
            id="folioRef"
            type="number"
            value={value?.folioRef ?? ""}
            onChange={(e) =>
              handleChange("folioRef", parseInt(e.target.value) || 0)
            }
            placeholder="123"
            min={1}
          />
        </div>

        {/* Date */}
        <div className="space-y-2">
          <Label htmlFor="fechaRef">Fecha del Documento</Label>
          <Input
            id="fechaRef"
            type="date"
            value={value?.fechaRef ?? ""}
            onChange={(e) => handleChange("fechaRef", e.target.value)}
          />
        </div>

        {/* Reference Code */}
        <div className="space-y-2">
          <Label>Razón de Referencia</Label>
          <Select
            value={value?.codigoRef?.toString() ?? ""}
            onValueChange={(v) =>
              handleChange("codigoRef", parseInt(v) as 1 | 2 | 3)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar razón" />
            </SelectTrigger>
            <SelectContent>
              {REFERENCE_CODES.map((code) => (
                <SelectItem key={code.value} value={code.value.toString()}>
                  {code.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Reason Text */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="razonRef">Descripción</Label>
          <Input
            id="razonRef"
            value={value?.razonRef ?? ""}
            onChange={(e) => handleChange("razonRef", e.target.value)}
            placeholder="ANULA FACTURA / CORRIGE GIRO DEL RECEPTOR / etc."
          />
        </div>
      </div>
    </div>
  );
}
